import React from "react";
import ItemSelector from "@ui/ItemSelector";
import AlertBadge from "./AlertBadge";
import { CalculatorLine, FilterLine } from "@ui/IconV2/react";
import { quantityColor } from "../useSelectedQuantities";
import QuantityIdentityCard from "./QuantityIdentityCard";
import type { Quantity } from "../../../api/useQuantities";
import type { SelectedQuantities } from "../useSelectedQuantities";
import type { ActivationFilter } from "../../../api/useActivationFilters";
import type { MetrologyCrossFilters } from "../../../api/useMetrologyCrossFilters";
import { styled } from "@linaria/react";
import { color, fonts } from "@ui";
import { css } from "@linaria/core";

interface SelectableQuantitiesGroupProps {
  projectId: string;
  quantities: Quantity[];
  selectedQuantities: SelectedQuantities;
  setSelectedQuantities: (newQuantities: Quantity[]) => void;
  showQuantityDescriptions: boolean;
  onlyFirstDisplayed: boolean;
  quantityAlerts: Record<string, { inRange: (boolean | null)[]; isInAlert: boolean }>;
  activationFiltersByQuantityId: { [quantityId: string]: ActivationFilter };
  crossFiltersByQuantityName: { [quantityName: string]: MetrologyCrossFilters };
  allAnalysesMode: boolean;
  alertType: "measures" | "symptoms";
  handleEditQuantity: (q: Quantity, focusFormulaeView?: boolean) => void;
  handleDeleteQuantity: (q: Quantity) => void;
  isDrawerOpen: boolean;
  getQuantityGroupLabel?: (quantity: Quantity) => string;
}

const SelectableQuantitiesGroup = ({
  projectId,
  quantities,
  selectedQuantities,
  setSelectedQuantities,
  showQuantityDescriptions,
  onlyFirstDisplayed,
  quantityAlerts,
  activationFiltersByQuantityId,
  crossFiltersByQuantityName,
  allAnalysesMode,
  alertType,
  handleEditQuantity,
  handleDeleteQuantity,
  isDrawerOpen,
  getQuantityGroupLabel,
}: SelectableQuantitiesGroupProps) => (
  <ItemSelector
    items={quantities.map(q => {
      const selectedQuantity = selectedQuantities.find(item => item.quantity.name === q.name);
      const isFirst = selectedQuantity === selectedQuantities[0];
      const color =
        !selectedQuantity || (onlyFirstDisplayed && !isFirst)
          ? undefined
          : quantityColor(selectedQuantity?.colorIndex);

      const inRangeAlerts = quantityAlerts[q.id]?.inRange ?? null;

      const alertCount = inRangeAlerts?.reduce((count, i) => (i ? ++count : count), 0) ?? 0;
      const analysisCount =
        inRangeAlerts?.reduce((count, i) => (i === null ? count : ++count), 0) ?? 0;
      const alertPercentage = analysisCount === 0 ? 0 : (alertCount / analysisCount) * 100;
      const isInAlert = quantityAlerts[q.id]?.isInAlert ?? false;

      const activationFilter = activationFiltersByQuantityId[q.id];
      const crossFilters = crossFiltersByQuantityName[q.name];
      const groupLabel = getQuantityGroupLabel?.(q);

      return {
        id: q.name,
        label: q.name,
        description: showQuantityDescriptions && q.description ? q.description : undefined,
        trailingContent: groupLabel ? (
          <span className={quantityGroupLabel} title={groupLabel}>
            {groupLabel}
          </span>
        ) : undefined,
        icons: (
          <>
            {q.formula !== null && (
              <div
                className={formulaIcon}
                {...(q.formulaVersionCount > 0 ? { "data-count": q.formulaVersionCount + 1 } : {})}
              >
                <PercentageIcon />
              </div>
            )}
            {activationFilter && <FilterLine />}
            {allAnalysesMode === false && (
              <AlertBadge
                withIcon={isInAlert}
                alertPercentage={alertPercentage}
                alertType={alertType}
              />
            )}
          </>
        ),
        color,
        overlayOptions: {
          quantity: q,
          projectId,
          activationFilter,
          crossFilters,
          alertMessageOptions: {
            allAnalysesMode,
            alertPercentage,
            alertCount,
            analysisCount,
            isInAlert,
            alertType,
          },
          onEditQuantity: handleEditQuantity,
          onDeleteQuantity: handleDeleteQuantity,
          isDrawerOpen,
        },
      };
    })}
    isSelectedItem={name => selectedQuantities.some(q => q.quantity.name === name)}
    toggleItem={name => {
      if (selectedQuantities.find(selectedQuantity => selectedQuantity.quantity.name === name)) {
        setSelectedQuantities(selectedQuantities.map(q => q.quantity).filter(q => q.name !== name));
      } else {
        const targetQuantity = quantities.find(q => q.name === name);
        if (targetQuantity !== undefined)
          setSelectedQuantities([...selectedQuantities.map(q => q.quantity), targetQuantity]);
      }
    }}
    setItem={name => {
      const targetQuantity = quantities.find(q => q.name === name);
      if (targetQuantity !== undefined) setSelectedQuantities([targetQuantity]);
    }}
    OverlayComponent={QuantityIdentityCard}
    fullWidth={false}
  />
);

//#region CSS
const quantityGroupLabel = css`
  color: ${color.carbon500};
  font: ${fonts.body2SemiBold};
`;

const PercentageIcon = styled(CalculatorLine)`
  height: 15px;
  width: 15px;
  margin-top: -2px;
  margin-bottom: -3px;
  color: ${color.carbon800};
`;

const formulaIcon = css`
  position: relative;
  &[data-count]:after {
    position: absolute;
    display: flex;
    justify-content: center;
    align-item: center;
    content: attr(data-count);
    width: 12px;
    height: 12px;
    border-radius: 6px;
    font-size: 8px;
    line-height: 12px;
    color: ${color.hydrogen};
    background-color: ${color.titanium800};
    top: 0;
    right: 0;
    transform: translate(35%, -10%);
  }
`;
//#endregion CSS

export default SelectableQuantitiesGroup;
